import { useMemo } from 'react';

import CheckboxField from '../../../../components/forms/CheckboxField';
import SearchSelect from '../../../../components/forms/SearchSelect';
import TextInput from '../../../../components/forms/TextInput';

import { BRREG_ENDPOINT, BRREG_ORGFORM_KODER } from './register.constants';

function mapEnhet(enhet) {
    return {
        name: enhet.navn,
        orgnr: enhet.organisasjonsnummer,
        form: enhet.organisasjonsform?.kode || null
    };
}

export default function BrregOrganizationField({ form, disableTab }) {
    const orgName = form.watch('organizationName');
    const useFreeText = form.watch('organizationUseFreeText');

    const loadOptions = useMemo(() => {
        return async (query, { signal }) => {
            const url = new URL(BRREG_ENDPOINT);
            url.searchParams.set('navn', query);
            url.searchParams.set('organisasjonsform', BRREG_ORGFORM_KODER.join(','));
            url.searchParams.set('page', '0');
            url.searchParams.set('size', '20');

            const res = await fetch(url.toString(), { signal });
            if (!res.ok) throw new Error(`Brreg-feil (${res.status})`);

            const json = await res.json();
            const enheter = json?._embedded?.enheter || [];
            return enheter.map(mapEnhet);
        };
    }, []);

    const toggleFreeText = e => {
        const checked = e.target.checked;

        // IKKE trigger validering ved toggle, for å unngå feil som dukker opp bare ved toggling.
        form.setValue('organizationUseFreeText', checked, { shouldDirty: true });

        if (checked) {
            // Når vi går til fritekst: nullstill valg
            form.setValue('organizationName', '', { shouldDirty: true });
            form.setValue('organizationSelectedKey', '', { shouldDirty: true });
        } else {
            // Tilbake til lookup: nullstill fritekst
            form.setValue('organizationFreeText', '', { shouldDirty: true });
        }

        // Oppdatere errors etter toggle
        form.clearErrors(['organizationName', 'organizationSelectedKey', 'organizationFreeText']);
    };

    return (
        <div className="org-field">
            {!useFreeText ? (
                <>
                    <SearchSelect
                        label="Organisasjon*"
                        name="organizationName"
                        autocomplete="organization"
                        disableTab={disableTab}
                        value={orgName || ''}
                        error={
                            form.formState.errors.organizationSelectedKey ||
                            form.formState.errors.organizationName
                        }
                        {...form.register('organizationName', {
                            // Ikke required her, ellers får vi feil bare ved typing/toggling.
                            maxLength: { value: 80, message: 'Organisasjonsnavnet er for langt.' },
                            onChange: e => {
                                // Hvis brukeren endrer tekst etter at de valgte noe, invalidér valget igjen.
                                form.setValue('organizationSelectedKey', '', { shouldDirty: true });
                                return e;
                            }
                        })}
                        minChars={2}
                        debounceMs={300}
                        cache
                        loadOptions={loadOptions}
                        getOptionKey={opt => opt.orgnr}
                        getOptionLabel={opt => opt.name}
                        getOptionSubLabel={opt =>
                            `Org.nr ${opt.orgnr}${opt.form ? ` · ${opt.form}` : ''}`
                        }
                        onSelect={opt => {
                            // Sett både display og “faktisk valg”
                            form.setValue('organizationName', opt.name, {
                                shouldDirty: true
                            });
                            form.setValue('organizationSelectedKey', opt.orgnr, {
                                shouldDirty: true,
                                shouldValidate: true // valider når vi faktisk har valgt
                            });
                            // Fjern evt. gammel feil
                            form.clearErrors('organizationSelectedKey');
                        }}
                        hint="Søk og velg fra Enhetsregisteret."
                    />

                    {/* Dette feltet er “sannheten” for at et valg er gjort */}
                    <input
                        type="hidden"
                        {...form.register('organizationSelectedKey', {
                            validate: val => {
                                // Bare krev valg når fritekst ikke er aktiv
                                if (form.getValues('organizationUseFreeText')) return true;
                                return !!val || 'Velg en organisasjon fra listen.';
                            }
                        })}
                    />
                </>
            ) : (
                <>
                    <TextInput
                        label="Organisasjon*"
                        name="organizationFreeText"
                        type="text"
                        autocomplete="organization"
                        error={form.formState.errors.organizationFreeText}
                        disableTab={disableTab}
                        {...form.register('organizationFreeText', {
                            required: 'Organisasjon er påkrevd.',
                            maxLength: { value: 80, message: 'Organisasjonsnavnet er for langt.' }
                        })}
                        hint="Skriv inn organisasjonsnavn manuelt."
                    />

                    <input type="hidden" {...form.register('organizationName')} />
                    <input type="hidden" {...form.register('organizationSelectedKey')} />
                </>
            )}

            <CheckboxField
                className="org-fallback"
                name="organizationUseFreeText"
                label="Jeg finner ikke organisasjonen min – jeg vil skrive den inn manuelt."
                disableTab={disableTab}
                {...form.register('organizationUseFreeText')}
                checked={!!useFreeText}
                onChange={toggleFreeText}
            />
        </div>
    );
}
